import DatePicker, { CalendarContainer, ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, ChevronIcon } from '../../../../bombiix-icon';

export interface NewDatePickerProps {
  /** Single-date value. Ignored when `selectsRange` is true. */
  value?: Date | null;
  /** Single-date change handler. Ignored when `selectsRange` is true. */
  onChange?: (_date: Date | null) => void;
  /** Enable range mode — pair with `startDate`/`endDate`/`onRangeChange`. */
  selectsRange?: boolean;
  /** Range mode start date. */
  startDate?: Date | null;
  /** Range mode end date. */
  endDate?: Date | null;
  /** Range mode change handler — fires when Set Date is confirmed. */
  onRangeChange?: (_start: Date | null, _end: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  filterDate?: (_date: Date) => boolean;
  invalid?: boolean;
  /** Applies the blue-outline highlight skin (Figma bulk-update visual). */
  isHighlight?: boolean;
  popperPlacement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'auto';
  portalId?: string;
  /**
   * Single-date mode only: apply the date immediately on day-click and close,
   * with no "Set Date" confirm button. Ignored when `selectsRange` is set.
   */
  autoApply?: boolean;
}

interface NewDateTriggerProps {
  value?: string;
  /** Overrides the react-datepicker-injected `value` — required in range mode
   * because react-datepicker forces a `"start - end"` string that we don't
   * want to show. */
  displayValue?: string;
  onClick?: () => void;
  placeholder?: string | undefined;
  disabled?: boolean;
  isOpen?: boolean;
  invalid?: boolean;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Which grid the calendar popup is showing: day cells, month cells, or year cells. */
type PickerView = 'day' | 'month' | 'year';

/**
 * Days to probe outward before treating a direction as unbounded. Kept small
 * (a bit over a month) so the derivation is cheap: a range whose boundary is
 * further out than this reads as "no bound that side", which is fine — the goal
 * is only to disable clearly out-of-range months/years, not to pinpoint a far
 * edge. Also the threshold that separates a genuine range bound from a
 * recurring-pattern filter (weekends, specific weekdays): a block found within
 * this window on BOTH sides means the filter is not a simple range.
 */
const FILTER_PROBE_DAYS = 40;

/**
 * Derive an effective `[minDate, maxDate]` from a `filterDate` predicate.
 *
 * In react-datepicker v4.1.1 the YEAR grid disables cells by `minDate`/`maxDate`
 * only — it never consults `filterDate`. The MONTH grid does consult
 * `filterDate`, but per a representative day, so a partially-past month (e.g.
 * the current one) greys out wholesale. So a caller that constrains dates purely
 * through `filterDate` (e.g. "today or later") gets a correct day grid, an
 * over-greyed month grid, and a fully-clickable year grid. Deriving min/max from
 * the filter lets both the month and year grids disable at the right grain.
 *
 * This scans day-by-day outward from an anchored "today" and returns the exact
 * day boundary of the first CONTIGUOUS allowed run in each direction — the
 * common shape for `filterDate` bounds ("on/after X", "on/before Y", or a
 * window). Callers snap these raw day bounds to month or year granularity as
 * needed (see `snapBoundsToMonth`/`snapBoundsToYear`) before handing them to
 * react-datepicker for the month/year grids; the day grid keeps `filterDate`
 * for exact-day precision.
 *
 * Guards against non-range filters:
 *  • If today itself is filtered out, derive nothing — the filter is not a
 *    simple range anchored near today.
 *  • A side whose allowed run reaches the probe horizon is left unbounded
 *    (`null`).
 *  • If BOTH sides block within the probe window, the filter is a recurring
 *    pattern (not a range) — derive nothing.
 */
function deriveBoundsFromFilter(filterDate: (_date: Date) => boolean): { min: Date | null; max: Date | null } {
  const anchor = new Date();
  anchor.setHours(0, 0, 0, 0);

  if (!filterDate(anchor)) return { min: null, max: null };

  let max: Date | null = null;
  for (let offset = 1; offset <= FILTER_PROBE_DAYS; offset += 1) {
    const forward = new Date(anchor);
    forward.setDate(anchor.getDate() + offset);
    if (!filterDate(forward)) {
      max = new Date(anchor);
      max.setDate(anchor.getDate() + offset - 1);
      break;
    }
  }

  let min: Date | null = null;
  for (let offset = 1; offset <= FILTER_PROBE_DAYS; offset += 1) {
    const backward = new Date(anchor);
    backward.setDate(anchor.getDate() - offset);
    if (!filterDate(backward)) {
      min = new Date(anchor);
      min.setDate(anchor.getDate() - offset + 1);
      break;
    }
  }

  if (min !== null && max !== null) return { min: null, max: null };

  return { min, max };
}

/**
 * Snap a derived min/max bound pair to whole-MONTH edges for the month grid.
 * react-datepicker disables a month cell when its first day is out of range, so
 * a `min` snapped to the 1st of its month keeps THAT month (e.g. the current
 * month) selectable while greying the earlier months; `max` snaps to month end.
 */
function snapBoundsToMonth(bounds: { min: Date | null; max: Date | null }): { min?: Date | undefined; max?: Date | undefined } {
  const min = bounds.min ? new Date(bounds.min.getFullYear(), bounds.min.getMonth(), 1) : undefined;
  const max = bounds.max ? new Date(bounds.max.getFullYear(), bounds.max.getMonth() + 1, 0) : undefined;
  return { min, max };
}

/**
 * Snap a derived min/max bound pair to whole-YEAR edges for the year grid.
 * react-datepicker disables a year cell when its Jan 1 is out of range, so a
 * `min` snapped to Jan 1 of its year keeps THAT year (e.g. the current year)
 * selectable while greying earlier years; `max` snaps to Dec 31.
 */
function snapBoundsToYear(bounds: { min: Date | null; max: Date | null }): { min?: Date | undefined; max?: Date | undefined } {
  const min = bounds.min ? new Date(bounds.min.getFullYear(), 0, 1) : undefined;
  const max = bounds.max ? new Date(bounds.max.getFullYear(), 11, 31) : undefined;
  return { min, max };
}

/**
 * Format a date as `DD.Month.YY` — matches the Figma range spec
 * (e.g. `18.August.25`). Falls back to an empty string for null.
 */
function formatRangeDate(date: Date | null | undefined): string {
  if (!date) return '';
  const day = date.getDate();
  const month = MONTH_NAMES[date.getMonth()];
  const year = String(date.getFullYear() % 100).padStart(2, '0');
  return `${day}.${month}.${year}`;
}

const NewDateTrigger = forwardRef<HTMLButtonElement, NewDateTriggerProps>(
  function NewDateTrigger({ value, displayValue, onClick, placeholder, disabled, isOpen, invalid }, ref) {
    const text = displayValue !== undefined ? displayValue : value;
    const isEmpty = !text;
    const triggerClass = [
      'bombiix-date-trigger',
      isEmpty ? 'bombiix-date-trigger--empty' : '',
      isOpen ? 'bombiix-date-trigger--open' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <button ref={ref} type="button" className={triggerClass} onClick={onClick} disabled={disabled}>
        <span className="bombiix-date-trigger__text">{text || placeholder}</span>
        {invalid ? (
          <span className="bombiix-date-trigger__alert-icon" aria-hidden="true">
            <i className="bombiix-icon-validation f-20" />
          </span>
        ) : null}
        <span className="bombiix-date-trigger__icon" aria-hidden="true">
          <CalendarIcon size={24} />
        </span>
      </button>
  );
});

/** Static labels the custom header needs. react-datepicker invokes the header
 * as a plain render callback where hooks are unavailable, so labels are passed in. */
interface HeaderLabels {
  prevMonth: string;
  nextMonth: string;
  monthView: string;
  yearView: string;
}

/**
 * Custom month/year header. The month and year are clickable buttons that open
 * the month-grid and year-grid views respectively (drill-down quick-nav). The
 * chevrons step by month in the day view and by year in the month/year views,
 * mirroring the legacy CustomDatePicker. The current view and its setter are
 * passed in because react-datepicker invokes this as a plain render callback,
 * where hooks are unavailable.
 */
function renderCustomHeader(
  { date, decreaseMonth, increaseMonth, decreaseYear, increaseYear }: ReactDatePickerCustomHeaderProps,
  view: PickerView,
  setView: (_view: PickerView) => void,
  labels: HeaderLabels,
) {
  const isDayView = view === 'day';
  const onPrev = isDayView ? decreaseMonth : decreaseYear;
  const onNext = isDayView ? increaseMonth : increaseYear;
  return (
    <div className="bombiix-datepicker__header-bar">
      <button
        type="button"
        className="bombiix-datepicker__nav-btn bombiix-datepicker__nav-btn--prev"
        onClick={onPrev}
        aria-label={labels.prevMonth}
      >
        <ChevronIcon size={24} />
      </button>
      <div className="bombiix-datepicker__month-year">
        {isDayView ? (
          <button
            type="button"
            className="bombiix-datepicker__month bombiix-datepicker__view-btn"
            onClick={() => setView('month')}
            aria-label={labels.monthView}
          >
            {MONTH_NAMES[date.getMonth()]}
          </button>
        ) : null}
        <button
          type="button"
          className="bombiix-datepicker__year bombiix-datepicker__view-btn"
          onClick={() => setView('year')}
          aria-label={labels.yearView}
        >
          {date.getFullYear()}
        </button>
      </div>
      <button
        type="button"
        className="bombiix-datepicker__nav-btn bombiix-datepicker__nav-btn--next"
        onClick={onNext}
        aria-label={labels.nextMonth}
      >
        <ChevronIcon size={24} />
      </button>
    </div>
  );
}

/**
 * NewDatePicker — Figma-spec date picker (node 5030:403001).
 * Two variants:
 *   • Single date (default) — `value` + `onChange`.
 *   • Date range — pass `selectsRange`, `startDate`, `endDate`, `onRangeChange`.
 * Calendar-icon trigger, custom month header, full-width Set Date confirmation.
 */
export function NewDatePicker({
  value,
  onChange,
  selectsRange,
  startDate,
  endDate,
  onRangeChange,
  placeholder,
  disabled,
  dateFormat = 'dd-MMM-yyyy',
  minDate,
  maxDate,
  filterDate,
  invalid,
  isHighlight,
  popperPlacement = 'bottom-start',
  portalId,
  autoApply = false,
}: NewDatePickerProps) {
  const { t: tRaw } = useTranslation('pim');
  const t = (key: string): string => String(tRaw(key));

  const [isOpen, setIsOpen] = useState(false);
  const [pickerView, setPickerView] = useState<PickerView>('day');

  /**
   * When only `filterDate` constrains the picker, derive raw day bounds from it
   * so react-datepicker can also disable the month/year grids (see
   * `deriveBoundsFromFilter`). An explicit `minDate`/`maxDate` from the caller
   * always takes precedence. Gated on `isOpen` — the bounds are only consumed
   * while the calendar renders, and consumers pass `filterDate` as an inline
   * arrow (new identity every render), so this keeps the day-by-day probe from
   * running on every render of a closed picker (e.g. idle grid cells).
   */
  const derivedBounds = useMemo(
    () => (isOpen && filterDate && !minDate && !maxDate ? deriveBoundsFromFilter(filterDate) : { min: null, max: null }),
    [isOpen, filterDate, minDate, maxDate],
  );

  /**
   * The month and year grids disable cells by min/max only, at their own
   * granularity — so the derived day bounds must be snapped to the active
   * grid's granularity (month grid → month edges, year grid → year edges) to
   * keep the CURRENT month/year selectable while greying earlier ones. The day
   * grid uses the caller's `filterDate` directly, so no derived bound there.
   * An explicit caller `minDate`/`maxDate` always wins.
   */
  const snappedBounds = useMemo(() => {
    if (pickerView === 'month') return snapBoundsToMonth(derivedBounds);
    if (pickerView === 'year') return snapBoundsToYear(derivedBounds);
    return { min: undefined, max: undefined };
  }, [pickerView, derivedBounds]);
  const effectiveMinDate = minDate ?? snappedBounds.min;
  const effectiveMaxDate = maxDate ?? snappedBounds.max;

  const [pendingDate, setPendingDate] = useState<Date | null>(value ?? null);
  const [pendingStart, setPendingStart] = useState<Date | null>(startDate ?? null);
  const [pendingEnd, setPendingEnd] = useState<Date | null>(endDate ?? null);

  useEffect(() => {
    if (!selectsRange) setPendingDate(value ?? null);
  }, [value, selectsRange]);

  useEffect(() => {
    if (selectsRange) {
      setPendingStart(startDate ?? null);
      setPendingEnd(endDate ?? null);
    }
  }, [startDate, endDate, selectsRange]);

  /**
   * A range needs two picks plus an explicit confirm, so the "Set Date" footer
   * is always shown in range mode; single-date mode shows it only when it is
   * not auto-applying.
   */
  const showFooter = selectsRange || !autoApply;
  const setDateLabel = t('PIM.DATE_PICKER.SET_DATE');

  const onConfirmRef = useRef<() => void>(() => undefined);
  onConfirmRef.current = () => {
    if (selectsRange) {
      if (onRangeChange) onRangeChange(pendingStart, pendingEnd);
    } else if (onChange) {
      onChange(pendingDate);
    }
    setPickerView('day');
    setIsOpen(false);
  };

  const CalendarWithFooter = useMemo(() => {
    function NewCalendarContainer({ className, children }: { className?: string | undefined; children?: React.ReactNode | undefined }) {
      return (
        <CalendarContainer className={className}>
          {children}
          {showFooter ? (
            <div className="bombiix-datepicker__footer">
              <button
                type="button"
                className="bombiix-btn btn-blue bombiix-datepicker__set-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onConfirmRef.current()}
              >
                {setDateLabel}
              </button>
            </div>
          ) : null}
        </CalendarContainer>
      );
    }
    NewCalendarContainer.displayName = 'NewCalendarContainer';
    return NewCalendarContainer;
  }, [showFooter, setDateLabel]);

  const handleSingleSelect = useCallback(
    (date: Date | null) => {
      if (pickerView === 'year') {
        setPickerView('month');
        return;
      }
      if (pickerView === 'month') {
        setPickerView('day');
        return;
      }
      if (autoApply) {
        onChange?.(date);
        setIsOpen(false);
        return;
      }
      setPendingDate(date);
    },
    [autoApply, onChange, pickerView],
  );

  const handleRangeSelect = useCallback(
    (dates: unknown) => {
      if (pickerView === 'year') {
        setPickerView('month');
        return;
      }
      if (pickerView === 'month') {
        setPickerView('day');
        return;
      }
      const range = Array.isArray(dates) ? (dates as (Date | null)[]) : [null, null];
      const [start = null, end = null] = range;
      setPendingStart(start);
      setPendingEnd(end);
    },
    [pickerView],
  );

  const handleClickOutside = useCallback(() => {
    if (selectsRange) {
      setPendingStart(startDate ?? null);
      setPendingEnd(endDate ?? null);
    } else {
      setPendingDate(value ?? null);
    }
    setPickerView('day');
    setIsOpen(false);
  }, [value, startDate, endDate, selectsRange]);

  const rootClass = ['bombiix-datepicker', invalid ? 'bombiix-datepicker--invalid' : '', isHighlight ? 'bombiix-datepicker--bulk' : '']
    .filter(Boolean)
    .join(' ');

  const triggerText = selectsRange ? (pendingStart || pendingEnd ? `${formatRangeDate(pendingStart)} to ${formatRangeDate(pendingEnd)}` : '') : undefined;

  const sharedProps = {
    open: isOpen,
    onInputClick: () => setIsOpen(true),
    onClickOutside: handleClickOutside,
    shouldCloseOnSelect: false,
    calendarContainer: CalendarWithFooter,
    calendarClassName: 'bombiix-datepicker__calendar',
    showMonthYearPicker: pickerView === 'month',
    showYearPicker: pickerView === 'year',
    showThreeColumnMonthYearPicker: true,
    yearItemNumber: 24,
    renderCustomHeader: (headerProps: ReactDatePickerCustomHeaderProps) =>
      renderCustomHeader(headerProps, pickerView, setPickerView, {
        prevMonth: t('PIM.DATE_PICKER.PREV_MONTH'),
        nextMonth: t('PIM.DATE_PICKER.NEXT_MONTH'),
        monthView: t('PIM.DATE_PICKER.MONTH_VIEW'),
        yearView: t('PIM.DATE_PICKER.YEAR_VIEW'),
      }),
    dateFormat,
    calendarStartDay: 1,
    disabled: Boolean(disabled),
    /* react-datepicker clones customInput and overwrites its `placeholder` prop with the
       DatePicker's `placeholderText`. Forward ours here so the trigger keeps our placeholder. */
    placeholderText: placeholder,
    minDate: effectiveMinDate,
    maxDate: effectiveMaxDate,
    /* react-datepicker (v4.1.1) applies `filterDate` to MONTH cells too, per a
       representative day — so a month holding a single filtered-out day (e.g.
       the current month, whose earlier days are past) greys out wholesale even
       though it has valid days. Restrict `filterDate` to the day grid; the
       month/year grids rely on the per-view snapped min/max above, which keeps
       the current month/year selectable while greying earlier ones. */
    filterDate: pickerView === 'day' ? filterDate : undefined,
    popperPlacement,
    portalId,
    showPopperArrow: false,
    popperModifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
    formatWeekDay: (day: string) => day.slice(0, 2).toLowerCase(),
  } as const;

  return (
    <div className={rootClass}>
      {selectsRange ? (
        <DatePicker
          {...sharedProps}
          selectsRange
          startDate={pendingStart ?? undefined}
          endDate={pendingEnd ?? undefined}
          onChange={handleRangeSelect}
          customInput={
            <NewDateTrigger
              displayValue={triggerText ?? ''}
              placeholder={placeholder}
              disabled={Boolean(disabled)}
              isOpen={isOpen}
              invalid={Boolean(invalid)}
            />
          }
        />
      ) : (
        <DatePicker
          {...sharedProps}
          selected={pendingDate}
          onChange={handleSingleSelect}
          customInput={<NewDateTrigger placeholder={placeholder} disabled={Boolean(disabled)} isOpen={isOpen} invalid={Boolean(invalid)} />}
        />
      )}
    </div>
  );
}

export default NewDatePicker;
